/**
 * Fifty Rooms welcome popup — behaviour for snippets/fr-popup.liquid (contract: docs/spec/progress/popup.md).
 *
 * Opens the <dialog id="FrPopup">:
 *  - automatically after data-delay seconds or on desktop exit intent, whichever comes first, at most once per
 *    data-days days, never for subscribers, never on cart / checkout / policy / account pages, never over
 *    another open dialog or drawer;
 *  - always on a click on [data-fr-popup-open] (announcement bar).
 * Subscribes through the standard Shopify customer form with fetch; falls back to a normal form submit when
 * Shopify answers with its captcha page (/challenge) or the request fails.
 *
 * hCaptcha: Shopify's storefront script binds customer forms (on first focus) — it replaces form.submit() with
 * "run the invisible hCaptcha, add h-captcha-response, call the previous submit()". We install our own
 * form.submit() first, so the chain ends in our fetch (or in the native submit for the fallback). Without the
 * token Shopify answers 400 "Missing CAPTCHA token". The dialog is opened with show(), not showModal(), so the
 * hCaptcha challenge (drawn in the page) stays above it and usable.
 */
const KEY_SEEN = 'fr_popup_seen';
const KEY_SUB = 'fr_popup_sub';
const KEY_PENDING = 'fr_popup_pending';
const QUIET_MS = 4000; // floor: nothing opens by itself in the first seconds
const RETRY_MS = 4000;
const MAX_RETRIES = 5;
const CAPTCHA_WAIT_MS = 8000;
const PENDING_LABEL_MS = 15000;
const DAY_MS = 864e5;
const BLOCKED_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?(?:cart|checkouts?|password|challenge|policies|account)(?:\/|$)/i;
const BLOCKERS =
  'dialog[open]:not(#FrPopup), [aria-modal="true"]:not(dialog):not([hidden]), details.menu-drawer-container[open]';
const ALREADY = /already|taken|subscribed|exists/i;

function read(key) {
  try {
    const value = window.localStorage.getItem(key);
    if (value !== null) return value;
  } catch {}
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function write(key, value, days) {
  try {
    window.localStorage.setItem(key, value);
    if (window.localStorage.getItem(key) === value) return;
  } catch {}
  try {
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${Math.round(days * 86400)}; SameSite=Lax`;
  } catch {}
}

function init(dialog) {
  const form = /** @type {HTMLFormElement | null} */ (dialog.querySelector('form'));
  const input = /** @type {HTMLInputElement | null} */ (dialog.querySelector('input[type="email"]'));
  const errorEl = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-fr-popup-error]'));
  const submit = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-fr-popup-submit]'));
  if (!form || !input || !errorEl || !submit) return;
  const backdrop = /** @type {HTMLElement | null} */ (document.querySelector('[data-fr-popup-backdrop]'));
  const pageWrapper = document.querySelector('.page-wrapper');
  const submitLabel = submit.textContent || '';

  const days = Math.max(1, Number(dialog.dataset.days) || 7);
  const delayMs = Math.max(QUIET_MS, (Number(dialog.dataset.delay) || 0) * 1000);
  let opener = null;
  let autoDone = false;
  let retries = 0;
  let busy = false;

  const setState = (state) => {
    dialog.dataset.state = state;
    dialog.querySelectorAll('[data-fr-popup-panel]').forEach((panel) => {
      panel.hidden = panel.getAttribute('data-fr-popup-panel') !== state;
    });
    dialog.setAttribute('aria-labelledby', state === 'success' ? 'FrPopupSuccessHeading' : 'FrPopupHeading');
  };

  const showError = (message) => {
    errorEl.textContent = message || errorEl.dataset.msgGeneric || '';
    errorEl.hidden = false;
    input.setAttribute('aria-invalid', 'true');
  };

  const clearError = () => {
    errorEl.textContent = '';
    errorEl.hidden = true;
    input.removeAttribute('aria-invalid');
  };

  /* Keeps the mobile sheet above the on-screen keyboard. */
  const onViewport = () => {
    const vv = window.visualViewport;
    if (!vv) return;
    const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    dialog.style.setProperty('--fr-popup-kb', `${Math.round(keyboard)}px`);
    dialog.style.maxHeight = keyboard > 80 ? `${Math.round(vv.height - 8)}px` : '';
  };

  const open = (mode, state) => {
    if (dialog.open) return;
    autoDone = true;
    opener = document.activeElement;
    if (state) setState(state);
    try {
      dialog.show();
    } catch {
      return;
    }
    if (backdrop) backdrop.hidden = false;
    pageWrapper?.setAttribute('inert', '');
    document.addEventListener('keydown', onKeydown);
    write(KEY_SEEN, String(Date.now()), days);
    window.visualViewport?.addEventListener('resize', onViewport);
    if (dialog.dataset.state === 'success') {
      /** @type {HTMLElement | null} */ (dialog.querySelector('#FrPopupSuccessHeading'))?.focus();
    } else if (mode === 'manual' && window.matchMedia('(pointer: fine)').matches) {
      input.focus();
    } else {
      // opened by itself: focus the heading (no keyboard pops up on phones, no focus ring on Close)
      /** @type {HTMLElement | null} */ (dialog.querySelector('#FrPopupHeading'))?.focus();
    }
    dialog.dispatchEvent(new CustomEvent('fr:popup:open', { bubbles: true, detail: { mode } }));
  };

  const close = () => {
    if (dialog.open) dialog.close();
  };

  dialog.addEventListener('close', () => {
    if (backdrop) backdrop.hidden = true;
    pageWrapper?.removeAttribute('inert');
    document.removeEventListener('keydown', onKeydown);
    window.visualViewport?.removeEventListener('resize', onViewport);
    dialog.style.removeProperty('--fr-popup-kb');
    dialog.style.maxHeight = '';
    write(KEY_SEEN, String(Date.now()), days);
    if (opener instanceof HTMLElement && opener.isConnected && opener !== document.body) opener.focus();
    opener = null;
  });

  dialog.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest('[data-fr-popup-close]')) close();
    else if (target.closest('[data-fr-popup-copy]')) void copy();
  });
  backdrop?.addEventListener('click', close);

  /* Esc closes; Tab cycles inside the dialog (the page behind is inert while it is open). */
  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      return close();
    }
    if (event.key !== 'Tab') return;
    const items = [...dialog.querySelectorAll('button, input:not([type="hidden"]), a[href]')].filter(
      (el) => el instanceof HTMLElement && !el.hasAttribute('disabled') && el.offsetParent !== null
    );
    if (!items.length) return;
    const first = /** @type {HTMLElement} */ (items[0]);
    const last = /** @type {HTMLElement} */ (items[items.length - 1]);
    const active = document.activeElement;
    if (active instanceof HTMLIFrameElement) return; // hCaptcha challenge
    const outside = !dialog.contains(active) || active === dialog || active?.id.endsWith('Heading');
    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  /** The code arrives only with a successful signup (not in the page HTML): show it, or the plain thanks. */
  const showCode = (code) => {
    const codeEl = dialog.querySelector('[data-fr-popup-code]');
    if (!codeEl) return;
    if (code) codeEl.textContent = code;
    const has = Boolean((codeEl.textContent || '').trim());
    const offer = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-fr-popup-offer]'));
    const plain = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-fr-popup-plain]'));
    if (offer) offer.hidden = !has;
    if (plain) plain.hidden = has;
  };

  const succeed = (code) => {
    write(KEY_SUB, '1', 365);
    showCode(code);
    clearError();
    setState('success');
    /** @type {HTMLElement | null} */ (dialog.querySelector('#FrPopupSuccessHeading'))?.focus();
  };

  /** Server-rendered result of the customer form (this page or a fetched one). */
  const serverResult = (root) => {
    const node = root.querySelector('#FrPopupForm [data-fr-popup-server]');
    if (!node) return null;
    const state = node.getAttribute('data-fr-popup-server');
    const message = (node.textContent || '').trim();
    if (state === 'success') return { type: 'success' };
    if (state === 'error') return ALREADY.test(message) ? { type: 'success' } : { type: 'error', message };
    return { type: 'none' };
  };

  const classify = async (response) => {
    const url = new URL(response.url || window.location.href, window.location.href);
    if (/\/challenge/.test(url.pathname)) return { type: 'fallback' };
    if (!response.ok) return { type: 'error' }; // 400 missing/invalid captcha token, 429, 5xx
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const code = (doc.querySelector('#FrPopup [data-fr-popup-code]')?.textContent || '').trim();
    if (url.searchParams.get('customer_posted') === 'true') return { type: 'success', code };
    const result = serverResult(doc);
    if (result?.type === 'success') return { type: 'success', code };
    // no popup form in the answer (captcha page) or no result on it → let the browser do a normal submit
    return !result || result.type === 'none' ? { type: 'fallback' } : result;
  };

  let labelTimer = 0;
  const setPending = (pending, lock) => {
    window.clearTimeout(labelTimer);
    submit.textContent = pending ? submit.dataset.labelSending || submitLabel : submitLabel;
    submit.disabled = Boolean(pending && lock);
    // the captcha step gives no "cancelled" signal: the button stays usable and the label resets by itself
    if (pending && !lock) labelTimer = window.setTimeout(() => setPending(false), PENDING_LABEL_MS);
  };

  let native = false;

  /** Runs form.submit() through Shopify's hCaptcha binding when there is one (see the header comment). */
  const submitWithCaptcha = () => {
    let started = false;
    const go = () => {
      if (started) return;
      started = true;
      form.submit();
    };
    const protect = /** @type {any} */ (window).Shopify?.captcha?.protect;
    if (form.hasAttribute('data-hcaptcha-bound') || typeof protect !== 'function') return go();
    try {
      protect(form, go); // binds the form (loads hCaptcha if needed), then calls back
    } catch {
      return go();
    }
    window.setTimeout(go, CAPTCHA_WAIT_MS);
  };

  const send = async () => {
    if (busy) return;
    busy = true;
    setPending(true, true);
    let outcome;
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      outcome = await classify(response);
    } catch {
      outcome = { type: 'fallback' };
    }
    busy = false;
    if (outcome.type === 'fallback') {
      // normal page submit with a fresh captcha token; the page navigates and comes back with customer_posted
      native = true;
      setPending(true, false);
      return submitWithCaptcha();
    }
    setPending(false);
    if (outcome.type === 'success') succeed(outcome.code);
    else {
      showError(outcome.message);
      input.focus();
    }
  };

  /* Our end of the submit chain; must be installed before Shopify's script binds the form. */
  form.submit = () => {
    if (!native) return void send();
    try {
      window.sessionStorage.setItem(KEY_PENDING, '1');
    } catch {}
    HTMLFormElement.prototype.submit.call(form);
  };

  /* Capture phase on the dialog: runs before (and instead of) the submit listeners Shopify adds to the form. */
  dialog.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopPropagation();
      if (busy) return;
      const email = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        showError(errorEl.dataset.msgEmail);
        input.focus();
        return;
      }
      input.value = email;
      clearError();
      native = false;
      setPending(true, false);
      submitWithCaptcha();
    },
    true
  );

  input.addEventListener('input', () => {
    if (!errorEl.hidden) clearError();
  });

  async function copy() {
    const codeEl = dialog.querySelector('[data-fr-popup-code]');
    const button = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-fr-popup-copy]'));
    const status = dialog.querySelector('[data-fr-popup-copy-status]');
    if (!codeEl || !button) return;
    const code = (codeEl.textContent || '').trim();
    let ok = false;
    try {
      await navigator.clipboard.writeText(code);
      ok = true;
    } catch {
      // fallback: a temporary field inside the dialog (the rest of the page is inert) + execCommand
      const field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
      dialog.appendChild(field);
      field.select();
      try {
        ok = document.execCommand('copy');
      } catch {}
      field.remove();
      button.focus();
    }
    if (!ok) {
      const range = document.createRange();
      range.selectNodeContents(codeEl);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    button.dataset.copied = String(ok);
    if (status) status.textContent = (ok ? button.dataset.labelCopied : button.dataset.labelFailed) || '';
  }

  /* Announcement bar and any other trigger: always opens, no frequency cap. */
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-fr-popup-open]') : null;
    if (!trigger) return;
    event.preventDefault();
    open('manual');
  });

  /* Return from a normal (non-AJAX) submit: ?customer_posted=true or the state rendered by Liquid. */
  let pending = false;
  try {
    pending = window.sessionStorage.getItem(KEY_PENDING) === '1';
    window.sessionStorage.removeItem(KEY_PENDING);
  } catch {}
  const mine = pending || window.location.hash === '#FrPopupForm';
  const params = new URLSearchParams(window.location.search);
  const posted = params.get('customer_posted') === 'true';
  const rendered = serverResult(document);
  if (posted || rendered?.type === 'success') {
    write(KEY_SUB, '1', 365); // also covers the footer signup form
    if (mine) {
      showCode();
      open('return', 'success');
      params.delete('customer_posted');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : ''));
    }
    return;
  }
  if (rendered?.type === 'error' && mine) {
    open('return', 'form');
    showError(rendered.message);
    return;
  }

  /* Automatic opening. */
  const allowed = () =>
    dialog.dataset.auto === 'true' &&
    dialog.dataset.subscribed !== 'true' &&
    !BLOCKED_PATH.test(window.location.pathname) &&
    !(/** @type {any} */ (window).Shopify?.designMode) &&
    !read(KEY_SUB) &&
    !(Date.now() - Number(read(KEY_SEEN) || 0) < days * DAY_MS);

  const busyPage = () => {
    if (document.hidden || document.querySelector(BLOCKERS)) return true;
    const active = document.activeElement;
    return Boolean(active && active.matches('input:not([type="checkbox"], [type="radio"]), textarea, select, [contenteditable="true"]'));
  };

  if (!allowed()) return;

  let timer = 0;
  const onExit = (event) => {
    if (event.relatedTarget || event.clientY > 0) return;
    tryAuto();
  };
  const stop = () => {
    window.clearTimeout(timer);
    document.removeEventListener('mouseout', onExit);
  };
  function tryAuto() {
    if (autoDone || dialog.open) return stop();
    if (!allowed()) {
      autoDone = true;
      return stop();
    }
    if (busyPage()) {
      window.clearTimeout(timer);
      retries += 1;
      if (retries > MAX_RETRIES) {
        autoDone = true;
        return stop();
      }
      timer = window.setTimeout(tryAuto, RETRY_MS);
      return;
    }
    stop();
    open('auto');
  }

  timer = window.setTimeout(tryAuto, delayMs);
  if (window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)').matches) {
    window.setTimeout(() => {
      if (!autoDone) document.addEventListener('mouseout', onExit);
    }, QUIET_MS);
  }
}

function boot() {
  const dialog = document.getElementById('FrPopup');
  if (dialog instanceof HTMLDialogElement && !dialog.dataset.ready) {
    dialog.dataset.ready = 'true';
    init(dialog);
  }
}

/* Nothing runs before the browser is idle; a click on the trigger before that boots immediately. */
const early = (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('[data-fr-popup-open]')) return;
  boot();
};
document.addEventListener('click', early, { capture: true, once: false });
const start = () => {
  document.removeEventListener('click', early, { capture: true });
  boot();
};
if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 3000 });
else window.setTimeout(start, 1500);
